/**
 * Dual-purpose Timeline: Activity (unified analytics timeline) + Upcoming (compliance deadlines).
 * Activity tab has filter chips and a summary strip; Upcoming shows future due items grouped by date.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTimeline, getComplianceDeadlines } from '../../api/analytics';
import { PageHeader } from '../ui';
import {
  Calendar,
  RefreshCw,
  Activity,
  Lightbulb,
  TrendingUp,
  AlertTriangle,
  ChevronRight,
  FileText,
  Truck,
  Shield,
  Clock,
} from 'lucide-react';
import '../../App.css';

const TAB_ACTIVITY = 'activity';
const TAB_UPCOMING = 'upcoming';

const ENTRY_ICONS = {
  event: Activity,
  insight: Lightbulb,
  improvement: TrendingUp,
  risk: AlertTriangle,
};

const ENTRY_TYPE_LABELS = {
  event: 'Activity',
  insight: 'Insight',
  improvement: 'Improvement',
  risk: 'Attention',
};

const FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'risks', label: 'Risks' },
  { id: 'improvements', label: 'Improvements' },
  { id: 'documents', label: 'Documents' },
  { id: 'vehicles', label: 'Vehicles' },
  { id: 'authority', label: 'Authority' },
  { id: 'insights', label: 'Insights' },
];

function matchesFilter(entry, filterId) {
  if (filterId === 'all') return true;
  if (filterId === 'risks') return entry.type === 'risk';
  if (filterId === 'improvements') return entry.type === 'improvement';
  if (filterId === 'insights') return entry.type === 'insight';
  if (filterId === 'documents') return entry.entityType === 'document';
  if (filterId === 'vehicles') return entry.entityType === 'vehicle';
  if (filterId === 'authority') return entry.entityType === 'authority';
  return true;
}

function groupEntriesByDate(entries) {
  const byDate = {};
  (entries || []).forEach((entry) => {
    const day = entry.timestamp?.slice(0, 10) || entry.date?.slice(0, 10);
    if (!day) return;
    if (!byDate[day]) byDate[day] = [];
    byDate[day].push(entry);
  });
  return byDate;
}

function formatDateHeading(isoDate, isUpcoming = false) {
  const d = new Date(isoDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  if (isUpcoming) {
    if (diff === 0) return { label, sub: 'Today' };
    if (diff === 1) return { label, sub: 'Tomorrow' };
    if (diff > 0 && diff <= 7) return { label, sub: `In ${diff} days` };
    if (diff > 7 && diff <= 30) return { label, sub: `In ${diff} days` };
    return { label, sub: '' };
  }
  if (diff === 0) return { label, sub: 'Today' };
  if (diff === 1) return { label, sub: 'Yesterday' };
  if (diff < 7) return { label, sub: `${-diff} days ago` };
  return { label, sub: '' };
}

function formatTime(isoTimestamp) {
  const d = new Date(isoTimestamp);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const SEVERITY_COLORS = { low: '#10B981', med: '#F59E0B', high: '#EF4444' };

export default function ComplianceTimelinePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(TAB_ACTIVITY);
  const [activityFilter, setActivityFilter] = useState('all');

  // Activity tab state
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityPage, setActivityPage] = useState(1);
  const activityLimit = 20;

  // Upcoming tab state
  const [upcomingData, setUpcomingData] = useState(null);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [upcomingRange, setUpcomingRange] = useState(90);

  const fetchActivity = useCallback(async (pageNum, append = false) => {
    setActivityLoading(true);
    try {
      const res = await getTimeline({ page: pageNum, limit: activityLimit });
      setActivityData((prev) => {
        if (!append || !prev?.entries?.length) return res;
        return { ...res, entries: [...prev.entries, ...res.entries] };
      });
    } catch (err) {
      console.error('Activity timeline error:', err);
      setActivityData((prev) => (prev || { entries: [], total: 0, page: 1, limit: activityLimit, hasMore: false }));
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const fetchUpcoming = useCallback(async () => {
    setUpcomingLoading(true);
    try {
      const res = await getComplianceDeadlines(upcomingRange);
      setUpcomingData(res);
    } catch (err) {
      console.error('Upcoming timeline error:', err);
      setUpcomingData({ events: [], summary: { next7: 0, next30: 0, next90: 0 } });
    } finally {
      setUpcomingLoading(false);
    }
  }, [upcomingRange]);

  useEffect(() => {
    if (activeTab === TAB_ACTIVITY) fetchActivity(activityPage, activityPage > 1);
  }, [activeTab, activityPage, fetchActivity]);

  useEffect(() => {
    if (activeTab === TAB_UPCOMING) fetchUpcoming();
  }, [activeTab, fetchUpcoming]);

  const handleRefresh = () => {
    if (activeTab === TAB_ACTIVITY) {
      setActivityPage(1);
      fetchActivity(1, false);
    } else {
      fetchUpcoming();
    }
  };

  // Summary from first batch of activity entries (improvements/risks/actions in last 7 days)
  const summary = useMemo(() => {
    const entries = activityData?.entries || [];
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const inWindow = (ts) => new Date(ts) >= sevenDaysAgo;
    let improvements = 0;
    let risks = 0;
    let actions = 0;
    entries.forEach((e) => {
      if (!inWindow(e.timestamp)) return;
      if (e.type === 'improvement') improvements += 1;
      else if (e.type === 'risk') risks += 1;
      else if (e.type === 'event') actions += 1;
    });
    return { improvements, risks, actions };
  }, [activityData?.entries]);

  // Filtered activity entries for display
  const filteredActivityEntries = useMemo(() => {
    const entries = activityData?.entries || [];
    return entries.filter((e) => matchesFilter(e, activityFilter));
  }, [activityData?.entries, activityFilter]);

  const activityByDate = groupEntriesByDate(filteredActivityEntries);
  const activitySortedDates = Object.keys(activityByDate).sort((a, b) => b.localeCompare(a));

  const upcomingEvents = upcomingData?.events || [];
  const upcomingByDate = groupEntriesByDate(upcomingEvents.map((e) => ({ ...e, timestamp: e.date })));
  const upcomingSortedDates = Object.keys(upcomingByDate).sort((a, b) => a.localeCompare(b));

  const loading = activeTab === TAB_ACTIVITY ? activityLoading : upcomingLoading;
  const initialLoad = activeTab === TAB_ACTIVITY ? !activityData : !upcomingData;

  return (
    <div className="compliance-timeline-page page-container">
      <PageHeader
        title="Compliance Timeline"
        subtitle="Activity history and upcoming deadlines"
        secondaryAction={{ label: 'Refresh', onClick: handleRefresh, icon: RefreshCw }}
      />

      <div className="timeline-tabs-wrap">
        <div className="ui-tabs timeline-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_ACTIVITY}
            className={`ui-tabs__tab ${activeTab === TAB_ACTIVITY ? 'ui-tabs__tab--active' : ''}`}
            onClick={() => setActiveTab(TAB_ACTIVITY)}
          >
            <Activity size={18} />
            Activity
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_UPCOMING}
            className={`ui-tabs__tab ${activeTab === TAB_UPCOMING ? 'ui-tabs__tab--active' : ''}`}
            onClick={() => setActiveTab(TAB_UPCOMING)}
          >
            <Clock size={18} />
            Upcoming
          </button>
        </div>
      </div>

      {activeTab === TAB_ACTIVITY && (
        <>
          {/* Summary strip */}
          <div className="timeline-summary-strip">
            <span className="timeline-summary-item">
              <TrendingUp size={16} />
              {summary.improvements} improvement{summary.improvements !== 1 ? 's' : ''} in last 7 days
            </span>
            <span className="timeline-summary-item">
              <AlertTriangle size={16} />
              {summary.risks} risk{summary.risks !== 1 ? 's' : ''} needing attention
            </span>
            <span className="timeline-summary-item">
              <Activity size={16} />
              {summary.actions} action{summary.actions !== 1 ? 's' : ''} completed
            </span>
          </div>

          {/* Filter chips */}
          <div className="timeline-filter-chips">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`timeline-filter-chip ${activityFilter === opt.id ? 'timeline-filter-chip--active' : ''}`}
                onClick={() => setActivityFilter(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}

      {activeTab === TAB_UPCOMING && (
        <div className="timeline-upcoming-controls">
          <label className="timeline-range-label">
            Show deadlines:
            <select
              value={upcomingRange}
              onChange={(e) => setUpcomingRange(Number(e.target.value))}
              className="timeline-range-select"
            >
              <option value={30}>Next 30 days</option>
              <option value={90}>Next 90 days</option>
              <option value={180}>Next 180 days</option>
            </select>
          </label>
          {upcomingData?.summary && (
            <span className="timeline-upcoming-pills">
              <span className="timeline-pill next7">7d: {upcomingData.summary.next7}</span>
              <span className="timeline-pill next30">30d: {upcomingData.summary.next30}</span>
              <span className="timeline-pill next90">90d: {upcomingData.summary.next90}</span>
            </span>
          )}
        </div>
      )}

      {loading && initialLoad ? (
        <div className="timeline-loading">
          <RefreshCw size={24} className="spin" />
          <p>Loading {activeTab === TAB_ACTIVITY ? 'activity' : 'upcoming'}...</p>
        </div>
      ) : activeTab === TAB_ACTIVITY ? (
        filteredActivityEntries.length === 0 ? (
          <div className="timeline-empty">
            <Calendar size={48} />
            <p>
              {activityFilter === 'all'
                ? 'No timeline entries yet. Upload documents, update vehicles, or run the nightly snapshot to see activity here.'
                : `No ${activityFilter} in recent activity. Try "All" or refresh.`}
            </p>
            <button type="button" className="button secondary" onClick={() => navigate('/documents')}>
              Go to Documents
            </button>
          </div>
        ) : (
          <>
            <div className="timeline-list">
              {activitySortedDates.map((dateStr) => {
                const { label, sub } = formatDateHeading(dateStr, false);
                return (
                  <section key={dateStr} className="timeline-date-group">
                    <h2 className="timeline-date-heading">
                      <Calendar size={18} />
                      {label}
                      {sub && <span className="timeline-date-sub">{sub}</span>}
                    </h2>
                    <ul className="timeline-events">
                      {activityByDate[dateStr].map((entry) => {
                        const Icon = ENTRY_ICONS[entry.type] || Activity;
                        const typeLabel = ENTRY_TYPE_LABELS[entry.type] || entry.type;
                        return (
                          <li
                            key={entry.id}
                            className={`timeline-event timeline-event--${entry.type}`}
                            role={entry.actionRoute ? 'button' : null}
                            tabIndex={entry.actionRoute ? 0 : undefined}
                            onClick={() => entry.actionRoute && navigate(entry.actionRoute)}
                            onKeyDown={(e) => {
                              if (entry.actionRoute && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                navigate(entry.actionRoute);
                              }
                            }}
                          >
                            <div className="timeline-event-icon" aria-hidden>
                              <Icon size={20} />
                            </div>
                            <div className="timeline-event-main">
                              <span className="timeline-event-type">{typeLabel}</span>
                              <span className="timeline-event-title">{entry.title}</span>
                              {entry.description && (
                                <span className="timeline-event-desc">{entry.description}</span>
                              )}
                              <span className="timeline-event-time">{formatTime(entry.timestamp)}</span>
                            </div>
                            {entry.actionRoute && (
                              <div className="timeline-event-actions">
                                <span className="timeline-event-link">
                                  View <ChevronRight size={16} />
                                </span>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
            {activityData?.hasMore && (
              <div className="timeline-pagination">
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => setActivityPage((p) => p + 1)}
                  disabled={activityLoading}
                >
                  {activityLoading ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )
      ) : upcomingSortedDates.length === 0 ? (
        <div className="timeline-empty">
          <Calendar size={48} />
          <p>No upcoming deadlines in this range. You're in good shape.</p>
          <button type="button" className="button secondary" onClick={() => navigate('/documents')}>
            Go to Documents
          </button>
        </div>
      ) : (
        <div className="timeline-list timeline-list--upcoming">
          {upcomingSortedDates.map((dateStr) => {
            const { label, sub } = formatDateHeading(dateStr, true);
            return (
              <section key={dateStr} className="timeline-date-group">
                <h2 className="timeline-date-heading">
                  <Calendar size={18} />
                  {label}
                  {sub && <span className="timeline-date-sub">{sub}</span>}
                </h2>
                <ul className="timeline-events">
                  {upcomingByDate[dateStr].map((ev) => (
                    <li
                      key={ev.id}
                      className="timeline-event timeline-event--upcoming"
                      style={{ borderLeftColor: SEVERITY_COLORS[ev.severity] || '#6B7280' }}
                      role="button"
                      tabIndex={0}
                      onClick={() => ev.actionUrl && navigate(ev.actionUrl)}
                      onKeyDown={(e) => {
                        if (ev.actionUrl && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          navigate(ev.actionUrl);
                        }
                      }}
                    >
                      <div className="timeline-event-icon" aria-hidden>
                        {ev.type?.includes('vehicle') ? (
                          <Truck size={20} />
                        ) : ev.type?.includes('medical') || ev.type?.includes('insurance') ? (
                          <FileText size={20} />
                        ) : (
                          <Shield size={20} />
                        )}
                      </div>
                      <div className="timeline-event-main">
                        <span className="timeline-event-type timeline-event-type--severity" data-severity={ev.severity}>
                          {ev.severity}
                        </span>
                        <span className="timeline-event-title">{ev.title}</span>
                        <span className="timeline-event-time">Due</span>
                      </div>
                      <div className="timeline-event-actions">
                        <span className="timeline-event-link">
                          View <ChevronRight size={16} />
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
